<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useBasePriceBrutoStore } from "@/modules/base_price/brutoStore";
import {
	assetsOf,
	cpmStatsOf,
	monthsOf,
	subtreeCpmStats,
} from "@/modules/base_price/utils/brutoCpm";
import BrutoHierarchyRow, {
	BrutoTreeRow,
} from "@/modules/base_price/components/BrutoHierarchyRow.vue";

const { t } = useI18n();
const store = useBasePriceBrutoStore();

/**
 * The flat node list rebuilt as a tree, each node's own screens listed after
 * its child nodes — a screen is an editable target too, one level below the
 * node it hangs off.
 */
const tree = computed<BrutoTreeRow[]>(() => {
	const rows = new Map<number, BrutoTreeRow>();
	const stats = subtreeCpmStats(store.nodes);

	store.nodes.forEach((node) =>
		rows.set(node.hierarchy_node_id, {
			key: `node-${node.hierarchy_node_id}`,
			nodeId: node.hierarchy_node_id,
			assetId: null,
			name: node.name,
			cpm: node.cpm,
			stats: stats.get(node.hierarchy_node_id) ?? null,
			children: [],
		})
	);

	const roots: BrutoTreeRow[] = [];

	store.nodes.forEach((node) => {
		const row = rows.get(node.hierarchy_node_id)!;
		const parent = node.parent_id !== null ? rows.get(node.parent_id) : null;

		(parent ? parent.children : roots).push(row);
	});

	store.nodes.forEach((node) => {
		rows.get(node.hierarchy_node_id)!.children.push(
			...assetsOf(node).map(
				(asset): BrutoTreeRow => ({
					key: `asset-${asset.asset_id}`,
					nodeId: node.hierarchy_node_id,
					assetId: asset.asset_id,
					name: asset.name,
					cpm: asset.cpm,
					stats: cpmStatsOf([monthsOf(asset.cpm.monthly_cpm)]),
					children: [],
				})
			)
		);
	});

	return roots;
});
</script>

<template>
	<div class="d-flex flex-column h-100">
		<div
			class="tree-header d-flex align-center ga-2 px-3 py-2 text-caption text-uppercase text-medium-emphasis"
		>
			<span class="tree-label-header">{{
				t("base_price.bruto.sidebar.level")
			}}</span>
			<span class="tree-col-value">{{
				t("base_price.bruto.sidebar.average")
			}}</span>
			<span class="tree-col-value">{{
				t("base_price.bruto.sidebar.min")
			}}</span>
			<span class="tree-col-value">{{
				t("base_price.bruto.sidebar.max")
			}}</span>
			<span class="tree-col-value">
				{{ t("base_price.bruto.sidebar.set_here") }}
			</span>
		</div>

		<perfect-scrollbar class="flex-grow-1" :options="{ suppressScrollX: true }">
			<bruto-hierarchy-row
				v-for="row in tree"
				:key="row.key"
				:row="row"
				:depth="0"
			/>
		</perfect-scrollbar>

		<div
			class="legend d-flex flex-column ga-2 px-3 py-3 text-caption text-medium-emphasis"
		>
			<div class="d-flex align-center ga-2">
				<span class="legend-mark legend-mark--own" />
				{{ t("base_price.bruto.sidebar.legend_set") }}
			</div>
			<div class="d-flex align-center ga-2">
				<span class="legend-mark" />
				{{ t("base_price.bruto.sidebar.legend_inherited") }}
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
// Matches .tree-label in BrutoHierarchyRow.vue: same 310px cap, and 34px of
// lead-in (12px row padding + the 14px twisty + its 8px gap).
.tree-label-header {
	flex: 1 1 auto;
	min-width: 0;
	max-width: 310px;
	padding-left: 34px;
}

.tree-header {
	border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	background: rgba(var(--v-theme-on-surface), 0.02);
}

.tree-col-value {
	flex: 0 0 56px;
	text-align: right;
}

.legend {
	border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.legend-mark {
	width: 3px;
	height: 14px;
	flex: 0 0 auto;
	background: rgba(var(--v-theme-on-surface), 0.15);
}

.legend-mark--own {
	background: rgb(var(--v-theme-warning));
}
</style>
